/** Portaled list panels from {@link SearchableSelect} and {@link SearchableMultiSelect}. */
export const SEARCHABLE_SELECT_PANEL_SELECTOR = "[data-searchable-select-panel]";

/** Portaled SKU list from {@link AssemblySkuCombobox} (and similar floating combobox panels). */
export const FLOATING_COMBOBOX_PANEL_SELECTOR = "[data-floating-combobox-panel]";

/**
 * App switcher module list (see `AppSwitcher`). Intentionally **below** {@link HOVER_HINT_PORTAL_Z_INDEX} so row
 * descriptions from {@link HoverHintTrigger} render on top of the open menu.
 */
export const APP_SWITCHER_MENU_Z_INDEX = 3600;

/**
 * {@link AnchoredDetailCallout} and form callouts built on it. Below hover hints so ? glyphs inside a callout stay
 * readable; above in-page overlays through `z-[200]`.
 */
export const ANCHORED_DETAIL_CALLOUT_Z_INDEX = 3800;

/** Portaled hub modal backdrops (work order actions, etc.). Above page hover hints; below hover hints from inside the modal. */
export const HUB_MODAL_OVERLAY_SELECTOR = "[data-hub-modal-overlay]";
export const HUB_MODAL_OVERLAY_Z_INDEX = 4100;

/**
 * A second hub modal opened from inside another (split or combine while recount is already open).
 * Above the parent modal, still under in-modal hover hints and combobox panels.
 */
export const HUB_NESTED_MODAL_OVERLAY_Z_INDEX = 4140;

/** Portaled hover-hint panels (`title-help.tsx`). Nested hints inside a panel stack above the parent panel. */
export const HOVER_HINT_PANEL_SELECTOR = "[data-hub-hover-hint-panel]";
export const HOVER_HINT_NESTED_Z_OFFSET = 20;

/**
 * Z-index for portaled hover hints and read-only hover layers on the base page
 * (above callouts; below hub modals).
 */
export const HOVER_HINT_PORTAL_Z_INDEX = 3900;

/**
 * Z-index for hover hints whose anchor is inside {@link HUB_MODAL_OVERLAY_SELECTOR}.
 * Above the modal shell; below combobox panels so lists inside a modal stay clickable.
 */
export const HOVER_HINT_IN_MODAL_Z_INDEX = 4150;

/**
 * Z-index for portaled combobox, autocomplete, and menu panels (above hub modals so lists opened inside a modal
 * are not clipped behind the modal shell).
 */
export const FLOATING_POPOVER_Z_INDEX = 4200;

/**
 * Hover hints whose anchor sits inside a floating popover (calendar, menu, combobox panel).
 * Always above that popover; still below hub toasts.
 */
export const HOVER_HINT_IN_FLOATING_POPOVER_Z_INDEX =
  FLOATING_POPOVER_Z_INDEX + HOVER_HINT_NESTED_Z_OFFSET;

/**
 * Portaled hub toasts (`HubToast`). Above combobox panels and modals so errors from modal submits stay visible.
 */
export const HUB_TOAST_Z_INDEX = 4300;

/**
 * Highest z-index on a positioned ancestor of the hint anchor, ignoring other hover-hint panels
 * (those stack through {@link HOVER_HINT_NESTED_Z_OFFSET}).
 */
function maxPositionedAncestorZIndex(anchor: Element | null | undefined): number {
  if (anchor == null) {
    return 0;
  }
  const view = globalThis.window;
  if (view == null || typeof view.getComputedStyle !== "function") {
    return 0;
  }
  let max = 0;
  let node: Element | null = anchor.parentElement;
  while (node != null) {
    if (typeof node.matches === "function" && node.matches(HOVER_HINT_PANEL_SELECTOR)) {
      node = node.parentElement;
      continue;
    }
    const style = view.getComputedStyle(node);
    const position = style.position;
    if (position === "fixed" || position === "absolute" || position === "sticky" || position === "relative") {
      const z = Number.parseInt(style.zIndex, 10);
      if (Number.isFinite(z) && z > max) {
        max = z;
      }
    }
    node = node.parentElement;
  }
  return max;
}

/**
 * Page hint under modal; modal hint above modal. A hint whose anchor sits inside a higher surface
 * (floating calendar, menu, or combobox panel) paints above that surface. Explicit `override` is the
 * floor, not a cap: the hint still rises above its anchor's surface. Toasts stay above hints.
 */
export function resolveHoverHintPortalZIndex(anchor: Element | null | undefined, override?: number): number {
  const inModal = anchor != null && anchor.closest(HUB_MODAL_OVERLAY_SELECTOR) != null;
  const nestedInHint = anchor != null && anchor.closest(HOVER_HINT_PANEL_SELECTOR) != null;
  let base = override ?? (inModal ? HOVER_HINT_IN_MODAL_Z_INDEX : HOVER_HINT_PORTAL_Z_INDEX);
  const ancestorZ = maxPositionedAncestorZIndex(anchor);
  if (ancestorZ >= base) {
    base = ancestorZ + HOVER_HINT_NESTED_Z_OFFSET;
  }
  if (nestedInHint) {
    base += HOVER_HINT_NESTED_Z_OFFSET;
  }
  if (base >= HUB_TOAST_Z_INDEX) {
    return HUB_TOAST_Z_INDEX - 1;
  }
  return base;
}

const MIN_LIST_VIEW_PX = 80;

export type AnchoredListPanelPlacement = "below" | "above";

export type AnchoredListPanelBox = {
  top: number;
  left: number;
  width: number;
  /** Max height for the scrollable list region only (not including chrome). */
  listContentMaxHeight: number;
  placement: AnchoredListPanelPlacement;
};

type ComputeAnchoredListPanelArgs = {
  anchorRect: DOMRect;
  gap?: number;
  edge?: number;
  minWidth?: number;
  /**
   * Fixed chrome above the scrollable list inside the panel (for example the search input block
   * in SearchableSelect).
   */
  chromeHeight?: number;
  listMaxHeightCap: number;
};

/**
 * Computes fixed positioning for a list panel portaled to `document.body`: clamps horizontal
 * position into the viewport, prefers opening below the anchor, and flips above when there is not
 * enough room.
 */
export function computeAnchoredListPanel(options: ComputeAnchoredListPanelArgs): AnchoredListPanelBox {
  const gap = options.gap ?? 4;
  const edge = options.edge ?? 8;
  const minWidth = options.minWidth ?? 120;
  const chromeHeight = options.chromeHeight ?? 0;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const anchor = options.anchorRect;

  let width = Math.max(anchor.width, minWidth);
  width = Math.min(width, viewportWidth - 2 * edge);

  let left = anchor.left;
  left = Math.max(edge, Math.min(left, viewportWidth - width - edge));

  const listCap = options.listMaxHeightCap;

  const spaceBelowForList = viewportHeight - anchor.bottom - gap - edge - chromeHeight;
  const spaceAboveForList = anchor.top - gap - edge - chromeHeight;

  const preferBelow =
    spaceBelowForList >= MIN_LIST_VIEW_PX || spaceBelowForList >= spaceAboveForList;

  let placement: AnchoredListPanelPlacement;
  let top: number;
  let listContentMaxHeight: number;

  if (preferBelow) {
    placement = "below";
    top = anchor.bottom + gap;
    listContentMaxHeight = Math.min(
      listCap,
      Math.max(MIN_LIST_VIEW_PX, viewportHeight - top - chromeHeight - edge),
    );
  } else {
    placement = "above";
    listContentMaxHeight = Math.min(
      listCap,
      Math.max(MIN_LIST_VIEW_PX, spaceAboveForList),
    );
    const panelOuterHeight = chromeHeight + listContentMaxHeight;
    top = anchor.top - gap - panelOuterHeight;
    if (top < edge) {
      top = edge;
      listContentMaxHeight = Math.max(
        MIN_LIST_VIEW_PX,
        Math.min(listContentMaxHeight, anchor.top - gap - edge - chromeHeight),
      );
    }
  }

  return { top, left, width, listContentMaxHeight, placement };
}

/** Soft max width for plain-text hover hints (36rem). Rich bodies may expand up to the viewport. */
export const HOVER_HINT_SOFT_MAX_WIDTH_PX = 36 * 16;

export type HoverHintPlacementOptions = {
  /**
   * Scroll height of the tooltip body after layout. When set, below-vs-above uses this (clamped to the
   * viewport cap) instead of assuming the panel always needs the full max-height budget.
   */
  contentHeight?: number | null;
  /**
   * Scroll width of the tooltip body after layout (including overflow past a soft max-width). When set,
   * horizontal clamping uses this width, and `maxWidth` expands past the soft 36rem cap when the body
   * still fits in the viewport so the bordered shell covers wide tables.
   */
  contentWidth?: number | null;
};

/**
 * Positions a portaled hover hint: prefers below the anchor when the measured (or provisional) content
 * fits there; otherwise opens above. Returns `transform` for center alignment.
 */
export function computeHoverHintFixedStyle(
  anchorRect: DOMRect,
  align: "start" | "center" | "end",
  options?: HoverHintPlacementOptions,
): {
  top: number;
  left: number;
  maxWidth: number;
  /** Omitted when measured content fits in the viewport so the shell grows with the body. */
  maxHeight?: number;
  transform?: string;
} {
  const edge = 16;
  const gap = 6;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewportMaxWidth = viewportWidth - 2 * edge;
  const softMaxWidth = Math.min(HOVER_HINT_SOFT_MAX_WIDTH_PX, viewportMaxWidth);
  const measuredWidth = options?.contentWidth;
  // Soft 36rem wraps long plain text. Measured scroll width past that (wide tables) expands the shell
  // up to the viewport so overflow-visible content is not painted outside the white background.
  const maxWidth =
    measuredWidth != null && measuredWidth > softMaxWidth
      ? Math.min(viewportMaxWidth, measuredWidth)
      : softMaxWidth;
  const maxHeightCap = Math.min(viewportHeight * 0.7, 24 * 16);
  const viewportMax = viewportHeight - 2 * edge;

  // Clamp horizontally with the measured panel width when available; the soft max-width is usually
  // much wider than a short text panel, and clamping with the soft cap alone pins every right-side
  // anchor at the same x position.
  const panelWidth =
    measuredWidth != null && measuredWidth > 0 ? Math.min(measuredWidth, maxWidth) : maxWidth;

  let left: number;
  let transform: string | undefined;
  if (align === "center") {
    const centerX = anchorRect.left + anchorRect.width / 2;
    const halfW = panelWidth / 2;
    left = Math.max(edge + halfW, Math.min(centerX, viewportWidth - edge - halfW));
    transform = "translateX(-50%)";
  } else if (align === "end") {
    left = Math.max(edge, Math.min(anchorRect.right - panelWidth, viewportWidth - panelWidth - edge));
  } else {
    left = Math.max(edge, Math.min(anchorRect.left, viewportWidth - panelWidth - edge));
  }

  const spaceBelow = viewportHeight - anchorRect.bottom - gap - edge;
  const spaceAbove = anchorRect.top - gap - edge;

  const measured = options?.contentHeight;
  const contentNeed =
    measured != null && measured > 0
      ? Math.min(viewportHeight - 2 * edge, Math.max(MIN_LIST_VIEW_PX, measured))
      : null;

  let top: number;
  let maxHeight: number | undefined;

  if (contentNeed != null) {
    // Position from measured height when content fits; omit maxHeight so the bordered shell
    // grows with rich bodies (forecast build breakdown, nested arrival lists).
    if (contentNeed <= viewportMax) {
      if (spaceBelow >= contentNeed) {
        top = anchorRect.bottom + gap;
      } else if (spaceAbove >= contentNeed) {
        top = anchorRect.top - gap - contentNeed;
      } else {
        const belowTop = anchorRect.bottom + gap;
        const aboveTop = anchorRect.top - gap - contentNeed;
        if (belowTop + contentNeed <= viewportHeight - edge) {
          top = belowTop;
        } else if (aboveTop >= edge) {
          top = aboveTop;
        } else {
          top = Math.max(edge, viewportHeight - edge - contentNeed);
        }
      }
      maxHeight = undefined;
    } else {
      top = edge;
      maxHeight = viewportMax;
    }
  } else {
    let preferBelow: boolean;
    if (spaceBelow >= MIN_LIST_VIEW_PX || spaceBelow >= spaceAbove) {
      preferBelow = true;
    } else {
      preferBelow = false;
    }

    if (preferBelow) {
      top = anchorRect.bottom + gap;
      maxHeight = Math.min(maxHeightCap, Math.max(MIN_LIST_VIEW_PX, spaceBelow));
    } else {
      maxHeight = Math.min(maxHeightCap, Math.max(MIN_LIST_VIEW_PX, spaceAbove));
      top = anchorRect.top - gap - maxHeight;
      if (top < edge) {
        top = edge;
        maxHeight = Math.max(
          MIN_LIST_VIEW_PX,
          Math.min(maxHeightCap, anchorRect.top - gap - edge),
        );
      }
    }
  }

  return { top, left, maxWidth, maxHeight, transform };
}

/** Fixed box for a full dropdown menu (entire panel scrolls within `maxHeight` when needed). */
export type AnchoredMenuPanelBox = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: AnchoredListPanelPlacement;
  /** Bottom-aligns the panel to `top` when opening above the anchor (short menus hug the trigger). */
  transform?: string;
};

/**
 * Positions a fixed menu below or above the anchor, aligned to the anchor’s start or end edge (LTR),
 * clamped horizontally and vertically into the viewport.
 */
export function computeHorizontalMenuPanel(options: {
  anchorRect: DOMRect;
  menuWidth: number;
  horizontalAlign: "start" | "end";
  gap?: number;
  edge?: number;
  maxHeightCap?: number;
  /** When set, opens the menu above or below the anchor instead of picking from viewport space. */
  preferPlacement?: AnchoredListPanelPlacement;
}): AnchoredMenuPanelBox {
  const gap = options.gap ?? 4;
  const edge = options.edge ?? 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(options.menuWidth, viewportWidth - 2 * edge);
  const anchor = options.anchorRect;
  let left =
    options.horizontalAlign === "end" ? anchor.right - width : anchor.left;
  left = Math.max(edge, Math.min(left, viewportWidth - width - edge));

  const cap =
    options.maxHeightCap ?? Math.min(640, Math.max(MIN_LIST_VIEW_PX, viewportHeight - 2 * edge));
  const spaceBelow = viewportHeight - anchor.bottom - gap - edge;
  const spaceAbove = anchor.top - gap - edge;
  const preferBelow =
    options.preferPlacement === "below"
      ? true
      : options.preferPlacement === "above"
        ? false
        : spaceBelow >= MIN_LIST_VIEW_PX || spaceBelow >= spaceAbove;

  let top: number;
  let maxHeight: number;
  let placement: AnchoredListPanelPlacement;

  if (preferBelow) {
    placement = "below";
    top = anchor.bottom + gap;
    maxHeight = Math.min(cap, Math.max(MIN_LIST_VIEW_PX, viewportHeight - top - edge));
    return { top, left, width, maxHeight, placement };
  }

  placement = "above";
  maxHeight = Math.min(cap, Math.max(MIN_LIST_VIEW_PX, spaceAbove));
  if (anchor.top - gap - maxHeight < edge) {
    maxHeight = Math.max(MIN_LIST_VIEW_PX, anchor.top - gap - edge);
  }
  top = anchor.top - gap;
  return { top, left, width, maxHeight, placement, transform: "translateY(-100%)" };
}

/**
 * Positions a fixed detail panel beside an anchor. When {@link contentHeight} is known, sizes the panel
 * to that height when it fits in the viewport so scrollbars are avoided when there is room.
 */
export function computeAnchoredDetailPanel(options: {
  anchorRect: DOMRect;
  panelWidth: number;
  gap?: number;
  edge?: number;
  contentHeight?: number | null;
}): AnchoredMenuPanelBox {
  const gap = options.gap ?? 6;
  const edge = options.edge ?? 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(options.panelWidth, viewportWidth - 2 * edge);
  const anchor = options.anchorRect;

  let left = anchor.left;
  left = Math.max(edge, Math.min(left, viewportWidth - width - edge));

  const spaceBelow = viewportHeight - anchor.bottom - gap - edge;
  const spaceAbove = anchor.top - gap - edge;
  const preferBelow = spaceBelow >= MIN_LIST_VIEW_PX || spaceBelow >= spaceAbove;
  const viewportMax = viewportHeight - 2 * edge;

  const measured = options.contentHeight;
  const contentNeed =
    measured != null && measured > 0
      ? Math.min(viewportMax, Math.max(MIN_LIST_VIEW_PX, measured))
      : null;

  if (contentNeed == null) {
    return computeHorizontalMenuPanel({
      anchorRect: anchor,
      menuWidth: width,
      horizontalAlign: "start",
      gap,
      edge,
      maxHeightCap: viewportMax,
    });
  }

  let top: number;
  let maxHeight: number;
  let placement: AnchoredListPanelPlacement;

  if (contentNeed <= viewportMax) {
    if (spaceBelow >= contentNeed) {
      placement = "below";
      top = anchor.bottom + gap;
      maxHeight = contentNeed;
    } else if (spaceAbove >= contentNeed) {
      placement = "above";
      top = anchor.top - gap - contentNeed;
      maxHeight = contentNeed;
    } else {
      const belowTop = anchor.bottom + gap;
      const aboveTop = anchor.top - gap - contentNeed;
      if (belowTop + contentNeed <= viewportHeight - edge) {
        placement = "below";
        top = belowTop;
      } else if (aboveTop >= edge) {
        placement = "above";
        top = aboveTop;
      } else {
        placement = preferBelow ? "below" : "above";
        top = Math.max(edge, viewportHeight - edge - contentNeed);
      }
      maxHeight = contentNeed;
    }
  } else if (preferBelow) {
    placement = "below";
    top = anchor.bottom + gap;
    maxHeight = Math.max(MIN_LIST_VIEW_PX, spaceBelow);
  } else {
    placement = "above";
    maxHeight = Math.max(MIN_LIST_VIEW_PX, spaceAbove);
    top = anchor.top - gap - maxHeight;
    if (top < edge) {
      top = edge;
      maxHeight = Math.max(MIN_LIST_VIEW_PX, anchor.top - gap - edge);
    }
  }

  return { top, left, width, maxHeight, placement };
}

/**
 * Context-menu style anchor at the pointer: places the panel at (clientX, clientY), clamped into the viewport.
 *
 * Pass `estimatedMenuHeight` for single-item (or otherwise fixed-height) menus so "above" placement
 * uses the real menu height instead of {@link MIN_LIST_VIEW_PX} / maxHeightCap (which floats compact
 * menus far above the pointer when the click is near the bottom of the viewport).
 */
export function computePointAnchoredContextMenu(options: {
  clientX: number;
  clientY: number;
  menuWidth: number;
  maxHeightCap?: number;
  edge?: number;
  estimatedMenuHeight?: number;
}): AnchoredMenuPanelBox {
  const edge = options.edge ?? 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cap = options.maxHeightCap ?? 420;
  const width = Math.min(options.menuWidth, viewportWidth - 2 * edge);

  let left = options.clientX;
  if (left + width > viewportWidth - edge) {
    left = Math.max(edge, viewportWidth - width - edge);
  }
  if (left < edge) {
    left = edge;
  }

  const y = options.clientY;

  if (options.estimatedMenuHeight != null && options.estimatedMenuHeight > 0) {
    const menuHeight = options.estimatedMenuHeight;
    let top = y;
    let placement: AnchoredListPanelPlacement = "below";
    if (top + menuHeight > viewportHeight - edge) {
      top = Math.max(edge, y - menuHeight);
      placement = "above";
    }
    if (top < edge) {
      top = edge;
    }
    return { top, left, width, maxHeight: menuHeight, placement };
  }

  const spaceBelow = viewportHeight - y - edge;
  const spaceAbove = y - edge;
  const preferBelow =
    spaceBelow >= MIN_LIST_VIEW_PX || spaceBelow >= spaceAbove;

  let top: number;
  let maxHeight: number;
  let placement: AnchoredListPanelPlacement;

  if (preferBelow) {
    placement = "below";
    top = y;
    maxHeight = Math.min(cap, Math.max(MIN_LIST_VIEW_PX, spaceBelow));
    if (top + maxHeight > viewportHeight - edge) {
      maxHeight = Math.max(MIN_LIST_VIEW_PX, viewportHeight - edge - top);
    }
  } else {
    placement = "above";
    maxHeight = Math.min(cap, Math.max(MIN_LIST_VIEW_PX, spaceAbove));
    top = y - maxHeight;
    if (top < edge) {
      top = edge;
      maxHeight = Math.max(MIN_LIST_VIEW_PX, y - edge);
    }
  }

  return { top, left, width, maxHeight, placement };
}
